import {
  generateNewAccessToken,
  performLogout,
  setAuthTokens,
} from 'app/helpers/authHelper';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import instance from '../redux/instance';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // check if token is still valid.
  useEffect(() => {
    const refreshToken = Cookies.get('refreshToken');
    const accessToken = Cookies.get('accessToken');

    async function generateNewToken() {
      try {
        const newAccessToken = await generateNewAccessToken(dispatch);
        const { user_id, email } = jwtDecode(newAccessToken);
        setUser({
          userID: user_id,
          email,
        });
        setLoading(false);
      } catch (error) {
        // Logout on failure
        console.error('generateNewToken error: ', error);
        logout();
      }
    }

    if (refreshToken) {
      try {
        const currentTime = Date.now() / 1000;
        const { exp: refreshTokenExp } = jwtDecode(refreshToken);

        // Refresh token expired — force logout
        if (refreshTokenExp < currentTime) logout();
        else if (!accessToken) generateNewToken();
        else {
          const { exp: accessTokenExp } = jwtDecode(accessToken);

          // Access token expired — refresh
          if (accessTokenExp < currentTime) generateNewToken();
          else {
            instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            const { user_id, email } = jwtDecode(accessToken);
            setUser({
              userID: user_id,
              email,
            });
            setLoading(false);
          }
        }
      } catch (error) {
        // Logout on failure
        console.error('Token decoding error: ', error);
        logout();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // call this function to log in user.
  const login = ({ accessToken, refreshToken }) => {
    setAuthTokens(accessToken, refreshToken);
    const { user_id, email } = jwtDecode(accessToken);

    setUser({
      userID: user_id,
      email,
    });
    setLoading(false);
    navigate('/', { replace: true });
  };

  // call this function to sign out logged in user.
  const logout = async () => {
    await performLogout(() => setUser(null));
  };

  const value = useMemo(
    () => ({
      isAuthenticated: !!user,
      user,
      loading,
      login,
      logout,
      setUser,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
